import React, { useState, useMemo } from 'react';
import {Box, Button, Card, CardContent, Chip, Collapse, IconButton, Skeleton, styled, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import { User, useGetEmployeeReviewsByUserByUserIdQuery, useGetLifeWheelsByUserByUserIdQuery } from "../../redux/vmsApi";
import { useGetBSCsByUserQuery } from "../../redux/balancedScorecardApi";
import { useGetPEARByEmployeeQuery } from "../../redux/pearReviewApi";
import { PERFORMANCE_RATINGS, getGradeFromScore } from "../reviews/bscTypes";
import {useRouter} from "../../hooks/useRouter";

interface OwnProps {
  userData: User;
}

interface ExpandMoreProps {
  expand: boolean;
}

const ExpandMore = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'expand',
})<ExpandMoreProps>(({ theme, expand }) => ({
  transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const EmployeeCard: React.FC<OwnProps> = ({ userData }) => {
  const router = useRouter();
  const [expanded, setExpanded] = useState<boolean>(false);

  const { data: spearReports, isLoading: spearIsLoading } = useGetEmployeeReviewsByUserByUserIdQuery(
    {userId: userData.id ?? ''},
    { skip: !expanded }
  );

  const { data: lifeWheelReports, isLoading: lifeWheelIsLoading } = useGetLifeWheelsByUserByUserIdQuery(
    {userId: userData.id ?? ''},
    { skip: !expanded }
  );

  const { data: bscReports, isLoading: bscIsLoading } = useGetBSCsByUserQuery(
    {userId: userData.id ?? ''},
    { skip: !userData.id }
  );

  const { data: pearReports, isLoading: pearIsLoading } = useGetPEARByEmployeeQuery(
    { employeeId: userData.id ?? '' },
    { skip: !expanded }
  );

  // Get latest non-draft BSC
  const latestBSC = useMemo(() => {
    if (!bscReports || bscReports.length === 0) return null;
    const nonDraftBSCs = bscReports.filter((bsc) => bsc.status !== 'draft');
    if (nonDraftBSCs.length === 0) return null;
    // Sort by submittedAt or reviewPeriod descending
    return nonDraftBSCs.sort((a, b) => {
      const dateA = a.submittedAt || a.reviewPeriod || '';
      const dateB = b.submittedAt || b.reviewPeriod || '';
      return dateB.localeCompare(dateA);
    })[0];
  }, [bscReports]);

  // Get color based on score
  const getScoreColor = (score?: number): string => {
    if (!score) return '#9e9e9e';
    if (score >= 100) return '#4caf50';
    if (score >= 85) return '#8bc34a';
    if (score >= 70) return '#ffc107';
    if (score >= 60) return '#ff9800';
    return '#f44336';
  };

  // Get grade color
  const getGradeColor = (grade?: string): string => {
    if (!grade) return '#9e9e9e';
    const rating = PERFORMANCE_RATINGS.find(r => r.grade === grade);
    return rating?.colorCode || '#9e9e9e';
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Card sx={{ marginBottom: 5, borderLeft: latestBSC ? `4px solid ${getScoreColor(latestBSC.totalWeightedScore)}` : undefined }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {(!userData.name || bscIsLoading) ? (
            <Skeleton variant="rectangular" width={210} height={20} />
          ) : (
            <>
              <Typography variant="h6">{userData.name}</Typography>
              {latestBSC && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon fontSize="small" sx={{ color: getScoreColor(latestBSC.totalWeightedScore) }} />
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    sx={{ color: getScoreColor(latestBSC.totalWeightedScore) }}
                  >
                    {latestBSC.totalWeightedScore?.toFixed(1)}%
                  </Typography>
                  {latestBSC.finalGrade && (
                    <Chip
                      label={latestBSC.finalGrade}
                      size="small"
                      sx={{
                        bgcolor: getGradeColor(latestBSC.finalGrade),
                        color: 'white',
                        fontWeight: 'bold',
                        height: 22,
                        fontSize: '0.75rem',
                      }}
                    />
                  )}
                </Box>
              )}
              {!latestBSC && !bscIsLoading && (
                <Typography variant="caption" color="text.disabled">
                  No BSC
                </Typography>
              )}
            </>
          )}
        </Box>
        <ExpandMore
          expand={expanded}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
        >
          <ExpandMoreIcon />
        </ExpandMore>
      </CardContent>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          <Typography paragraph>SPEAR:</Typography>
          {spearReports && spearReports.map((report, index) => (
            <Button key={index} onClick={() => router.push(`/reviews/admin/spear/${report.id}`)}>
              {report.reviewPeriod}
            </Button>
          ))}
          <Typography paragraph>Life Wheel:</Typography>
          {lifeWheelReports && lifeWheelReports.map((report, index) => (
            <Button key={index} onClick={() => router.push(`/reviews/admin/life-wheel/${report.id}`)}>
              {report.reviewPeriod}
            </Button>
          ))}
          <Typography paragraph>BSC:</Typography>
          {bscReports && bscReports
            .filter((report) => report.status !== 'draft')
            .map((report, index) => (
              <Button key={index} onClick={() => router.push(`/reviews/admin/bsc/${report.id}`)}>
                {report.reviewPeriod}
              </Button>
            ))}
          <Typography paragraph>PEAR:</Typography>
          {pearIsLoading && <Typography variant="caption">Loading...</Typography>}
          {pearReports && pearReports
            .filter((report) => report.status !== 'draft')
            .map((report, index) => (
              <Button key={index} onClick={() => router.push(`/reviews/pear?period=${encodeURIComponent(report.reviewPeriod)}&from=admin`)}>
                {report.reviewPeriod} ({report.overallScore?.toFixed(1)}% - {report.finalGrade})
              </Button>
            ))}
          {pearReports && pearReports.filter((report) => report.status !== 'draft').length === 0 && !pearIsLoading && (
            <Typography variant="caption" color="text.secondary">No submitted PEAR reviews</Typography>
          )}
          <Typography paragraph>
            <Button onClick={() => router.push(`/reviews/admin/360/${userData.id}`)}>
              View 360 Reviews
            </Button>
          </Typography>
        </CardContent>
      </Collapse>
    </Card>
  );
}

export default EmployeeCard;
